import cn from 'classnames';
import { forwardRef } from 'react';

import { visuallyHidden } from '../../../utils/dom';

import useSegmentedControl from './use-segmented-control';

import type { FocusEventHandler, ForwardedRef } from 'react';

import type { SegmentedControlProps } from './segmented-control.types';

import './segmented-control.scss';

/**
 * A radio-group-based control that presents a set of mutually exclusive choices
 * as a row of equal-width segments with an animated thumb indicator that slides
 * to the selected item.
 *
 * Supports controlled and uncontrolled usage, keyboard navigation (arrow keys,
 * Home/End, Space/Enter), full-width layout, two sizes (`md` and `sm`),
 * read-only and disabled states, icon-only segments, and native form
 * participation via hidden radio inputs.
 *
 * @example
 * ```tsx
 * <SegmentedControl
 *   items={[
 *     { value: 'day', label: 'Day' },
 *     { value: 'week', label: 'Week' },
 *     { value: 'month', label: 'Month' },
 *   ]}
 *   defaultValue="day"
 *   onChange={(value) => console.log(value)}
 * />
 * ```
 */
const SegmentedControl = forwardRef((props: SegmentedControlProps, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    className,
    defaultValue,
    disabled = false,
    form,
    fullWidth = false,
    items,
    name: nameProp,
    onBlur,
    onBlurCapture,
    onChange,
    onFocus,
    onFocusCapture,
    readOnly = false,
    size = 'md',
    value: valueProp,
    ...rest
  } = props;

  const {
    focusIndicatorStyle,
    handleBlur,
    handleFocus,
    handleKeyDown,
    handleSelect,
    isThumbTransitionDisabled,
    mergedRef,
    resolvedName,
    selectedValue,
    setItemRef,
    tabbableIndex,
    thumbStyle,
  } = useSegmentedControl({
    defaultValue,
    disabled,
    items,
    name: nameProp,
    onChange,
    readOnly,
    ref,
    value: valueProp,
  });

  const rootClasses = cn(
    'rbui-segmented-control',
    {
      'rbui-segmented-control--disabled': disabled,
      'rbui-segmented-control--full-width': fullWidth,
      'rbui-segmented-control--read-only': readOnly,
      'rbui-segmented-control--sm': size === 'sm',
    },
    className,
  );

  const handleRootBlurCapture: FocusEventHandler<HTMLDivElement> = (event) => {
    handleBlur(event);
    onBlurCapture?.(event);
  };

  const handleRootFocusCapture: FocusEventHandler<HTMLDivElement> = (event) => {
    handleFocus(event);
    onFocusCapture?.(event);
  };

  return (
    <div
      {...rest}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-readonly={readOnly || undefined}
      className={rootClasses}
      onBlur={onBlur}
      onBlurCapture={handleRootBlurCapture}
      onFocus={onFocus}
      onFocusCapture={handleRootFocusCapture}
      ref={mergedRef}
      role="radiogroup"
      tabIndex={-1}
    >
      {focusIndicatorStyle && (
        <div
          aria-hidden="true"
          className={cn('rbui-segmented-control__focus-indicator', {
            'rbui-segmented-control__focus-indicator--no-transition': isThumbTransitionDisabled,
          })}
          style={focusIndicatorStyle}
        />
      )}

      {thumbStyle && (
        <div
          aria-hidden="true"
          className={cn('rbui-segmented-control__thumb', {
            'rbui-segmented-control__thumb--no-transition': isThumbTransitionDisabled,
          })}
          style={thumbStyle}
        />
      )}

      {items.map((item, index) => {
        const isSelected = item.value === selectedValue;
        const isItemDisabled = disabled || !!item.disabled;
        const isIconOnly = !!item.icon && !item.label;

        const itemClasses = cn('rbui-segmented-control__item', {
          'rbui-segmented-control__item--disabled': isItemDisabled,
          'rbui-segmented-control__item--icon-only': isIconOnly,
          'rbui-segmented-control__item--selected': isSelected,
        });

        return (
          <div
            aria-checked={isSelected}
            aria-disabled={isItemDisabled || undefined}
            aria-label={item.ariaLabel}
            className={itemClasses}
            key={item.value}
            onClick={() => handleSelect(item.value, isItemDisabled)}
            onKeyDown={handleKeyDown}
            ref={(element) => setItemRef(index, element)}
            role="radio"
            tabIndex={index === tabbableIndex && !disabled ? 0 : -1}
          >
            <input
              aria-hidden="true"
              checked={isSelected}
              disabled={isItemDisabled}
              form={form}
              name={resolvedName}
              readOnly
              style={visuallyHidden}
              tabIndex={-1}
              type="radio"
              value={item.value}
            />
            {item.icon && (
              <span aria-hidden="true" className="rbui-segmented-control__icon">
                {item.icon}
              </span>
            )}
            {item.label && <span>{item.label}</span>}
          </div>
        );
      })}
    </div>
  );
});

SegmentedControl.displayName = 'SegmentedControl';

export default SegmentedControl;
